import { Observable, Subject } from 'rxjs';
import { Inject, Injectable } from '@angular/core';
import {
  Identity,
  IdentityService,
  TOKENS,
} from '@trustedshops/tswp-core-authorization';
import { KeycloakProfileData } from '@trustedshops/tswp-core-authorization-keycloak';
import { RxJsSubjectBridge } from '@trustedshops/tswp-core-common-eventing-rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
} from 'rxjs/operators';

/**
 * User profile data structure combining Keycloak token data
 * with application-specific user properties
 */
export interface UserProfile {
  /** Core Keycloak properties */
  identityRef: string; // Unique user identifier from Keycloak
  email: string; // User's email address
  given_name: string; // First name from Keycloak
  family_name: string; // Last name from Keycloak
  'https://etrusted.com'?: { // Custom claim namespace
    accountRef: string; // Account reference ID
  };
  createdAt: string; // ISO date when user was created
}

/**
 * Service responsible for managing user identity data from Keycloak.
 * Provides reactive streams of user information that auto-update when
 * the authentication token changes.
 */
@Injectable()
export class UserService {
  /**
   * Stream of user profile data enriched with application-specific fields
   */
  public readonly user$: Observable<UserProfile>;

  /**
   * Stream of the user's unique identifier.
   * Emits once and caches the value for performance.
   */
  public readonly userId$: Observable<string>;

  constructor(
    @Inject(TOKENS.IdentityService)
    private readonly identityService: IdentityService
  ) {
    // Initialize user data stream from Keycloak token
    this.user$ = this.extractUserFromKeycloakToken();

    // Extract and cache user ID for quick access
    this.userId$ = this.user$.pipe(
      map((user) => user?.identityRef),
      distinctUntilChanged(),
      shareReplay(1) // Cache for future subscribers
    );
  }

  /**
   * Extracts and enriches user profile from Keycloak token and session data.
   * Combines token payload with session information to build complete profile.
   *
   * @returns Observable stream of enriched user profile
   */
  private extractUserFromKeycloakToken(): Observable<UserProfile> {
    return this.identityService.identity
      .convertWith<Subject<Identity<KeycloakProfileData>>>(RxJsSubjectBridge)
      .pipe(
        // Only process when we have a valid identity
        filter(
          (identity): identity is Identity<KeycloakProfileData> => !!identity
        ),

        // Extract the token payload from the identity
        map((identity) => identity.profile.keycloak.token.payload),

        // Convert payload to observable stream
        switchMap((payload) =>
          payload.convertWith<Subject<UserProfile>>(RxJsSubjectBridge)
        ),

        // Only emit when user identity actually changes
        distinctUntilChanged(
          (prev, curr) => prev.identityRef === curr.identityRef
        ),

        // Enrich user data with session information
        switchMap((tokenUser) =>
          this.identityService.sessionProvider.parsedToken.pipe(
            // Wait for valid session data
            filter((token) => !!token),

            // Map and enrich the user profile
            map(({ kcUserCreated }) => ({
              ...tokenUser,
              createdAt: new Date(kcUserCreated).toISOString(),
            })),
          )
        ),

        // Cache the result and share among all subscribers
        shareReplay(1)
      );
  }
}
